import {
  BiddingSpecConfig,
  DeckSpecConfig,
  FeatureFlags,
  GameTableConfig,
  PhaseConfig,
  PlayRulesConfig,
  ScoringSpecConfig,
  TimingConfig,
  UiHints
} from './types';

/**
 * Generic Structural Defaults
 * 
 * Safe generic defaults used when normalizing partial or missing configurations.
 * These are generic card game structures (not Bid Whist or Spades rules).
 */

export const DEFAULT_TIMING: TimingConfig = {
  turnTimeoutSec: 20,
  biddingTimeoutSec: 40,
  trumpDeclarationTimeoutSec: 20,
  kittyDiscardTimeoutSec: 40,
  passingTimeoutSec: 20,
  dealAnimationMs: 6500,
  trickSweepDelayMs: 800,
  handRevealDelayMs: 1000,
  readyCountdownSec: 5,
  botBiddingDelayMs: 285,
  botPlayDelayMs: 360,
  botTrumpDelayMs: 420
};

export const GENERIC_DECK_CONFIG: DeckSpecConfig = {
  totalCards: 52,
  includeJokers: false,
  cardsPerPlayer: 13,
  kittyCards: 0,
  deckProfile: 'STANDARD'
};

export const GENERIC_PHASES: PhaseConfig[] = [
  { id: 'Dealing', timeoutSeconds: 8, isTurnBased: false },
  { id: 'Playing', timeoutSeconds: 20, isTurnBased: true },
  { id: 'HandEnd', timeoutSeconds: 15, isTurnBased: false },
  { id: 'GameOver', timeoutSeconds: 0, isTurnBased: false }
];

export const GENERIC_FEATURES: FeatureFlags = {
  hasKitty: false,
  hasTrumpDeclaration: false,
  hasKittyDiscard: false,
  hasDirectionBidding: false,
  hasBrokenTrumpRule: false,
  hasBagPenalty: false,
  hasClaimMechanic: false,
  canBoston: false,
  hasSingleContractBidder: false,
  hasNoTrumpOption: false,
  totalTricksPerHand: 13,
  totalBooksPerHand: 13,
  hasPassingPhase: false,
  canEndHandEarlyOnSet: false,
  defaultTrumpSuit: null,
  hasBidding: false
};

export const GENERIC_BIDDING: BiddingSpecConfig = {
  biddingModel: 'NONE',
  minBid: 0,
  maxBid: 0,
  allowPass: false,
  stickTheDealer: false,
  stuckDealerMinBid: 0,
  stuckDealerPassCount: 0,
  allowedBidTypes: [],
  nilBonusPoints: 0,
  nilPenaltyPoints: 0,
  allowBlindNil: false,
  blindNilBonusPoints: 0
};

export const GENERIC_SCORING: ScoringSpecConfig = {
  model: 'NONE',
  targetScore: 0,
  settlementType: 'NONE',
  pointsPerContractTrick: 0,
  pointsPerOvertrick: 0,
  bagPenaltyThreshold: 0,
  bagPenaltyPoints: 0,
  bostonBonusPoints: 0,
  booksToSetFormula: 'NONE',
  hasDoubleScoreOnNoTrump: false
};

export const GENERIC_PLAY_RULES: PlayRulesConfig = {
  mustFollowLedSuit: true,
  trumpLeadRestriction: 'NONE',
  voidSloughRestriction: 'NONE',
  canTrumpLeadIfOnlyTrumpInHand: true
};

export const GENERIC_UI_HINTS: UiHints = {
  scoreDisplayMode: 'POINTS_500',
  showContractHud: false,
  showBooksToSetCounter: false,
  showBagsCounter: false,
  showDirectionIndicator: false,
  targetScore: 0,
  trickNoun: 'trick',
  trickNounPlural: 'tricks',
  terminology: {
    trick: 'trick',
    trickPlural: 'tricks',
    round: 'hand',
    contract: 'contract',
    kitty: 'kitty',
    declarer: 'winning bidder',
    set: 'set'
  }
};

export const GENERIC_DEFAULT_CONFIG: GameTableConfig = {
  gameType: 'GENERIC',
  displayName: 'Card Game',
  deck: GENERIC_DECK_CONFIG,
  phases: GENERIC_PHASES,
  timing: DEFAULT_TIMING,
  features: GENERIC_FEATURES,
  bidding: GENERIC_BIDDING,
  scoring: GENERIC_SCORING,
  playRules: GENERIC_PLAY_RULES,
  ui: GENERIC_UI_HINTS,
  contractFormat: 'NONE',
  tableOptions: []
};
